/**
 * Shared focus/blur state for fields that render an `ExpressionPreview`.
 * Centralises the "don't blur while the user is selecting text
 * in the preview" guard that every field would otherwise duplicate.
 *
 * Usage:
 *   const { isFocused, containerHandlers, previewHandlers } = useFieldPreviewFocus();
 *
 *   <div {...containerHandlers}>
 *     <TokenizedInput ... />
 *     {isFocused && value && (
 *       <ExpressionPreview mode="overlay" {...previewHandlers} ... />
 *     )}
 *   </div>
 *
 * Render `<span ref={previewToggleSlotRef} />` in the field's label row to host
 * the Preview toggle there; the spread `previewHandlers` hands the slot to
 * `ExpressionPreview`, so the toggle never takes layout space below the input.
 */
import { useCallback, useMemo, useRef, useState, type FocusEvent } from 'react';

export interface UseFieldPreviewFocusResult {
  isFocused: boolean;
  containerHandlers: {
    onFocus: () => void;
    onBlur: (event: FocusEvent<HTMLElement>) => void;
  };
  previewHandlers: {
    onMouseDown: () => void;
    onMouseUp: () => void;
    onMouseLeave: () => void;
    toggleContainer: HTMLElement | null;
  };
  /** Callback ref for the label-row element that hosts the Preview toggle. */
  previewToggleSlotRef: (element: HTMLElement | null) => void;
}

export const useFieldPreviewFocus = (): UseFieldPreviewFocusResult => {
  const [isFocused, setIsFocused] = useState(false);
  // Tracks an in-progress mouse interaction inside the preview so we can
  // ignore the spurious blur the editor receives during text selection.
  const isInteractingRef = useRef(false);
  const [toggleContainer, setToggleContainer] = useState<HTMLElement | null>(null);

  const onFocus = useCallback(() => setIsFocused(true), []);

  const onBlur = useCallback((event: FocusEvent<HTMLElement>) => {
    const movingOutside = !event.currentTarget.contains(event.relatedTarget as Node | null);
    if (isInteractingRef.current && !movingOutside) return;
    if (movingOutside) setIsFocused(false);
  }, []);

  const previewHandlers = useMemo(
    () => ({
      onMouseDown: () => { isInteractingRef.current = true; },
      onMouseUp: () => { isInteractingRef.current = false; },
      onMouseLeave: () => { isInteractingRef.current = false; },
      toggleContainer,
    }),
    [toggleContainer],
  );

  return {
    isFocused,
    containerHandlers: { onFocus, onBlur },
    previewHandlers,
    previewToggleSlotRef: setToggleContainer,
  };
};
